/**
 * GLTF Utilities
 *
 * Helpers for parsing .gltf files, detecting missing external resource
 * references, and converting .gltf + resources into a single .glb file.
 */

import { GLTF } from './gltf2';

// Inspired by:
// - https://github.com/CesiumGS/gltf-pipeline/blob/0f963b95b402e4a4ddf1a6716c5e98c961e9e202/lib/gltfToGlb.js
// - https://github.com/BabylonJS/Babylon.js/blob/dd2eb63dd920f13cd7006fc89848d8fdd21bcdd7/packages/dev/serializers/src/glTF/2.0/glTFExporter.ts#L1191
// - https://github.com/visgl/loaders.gl/blob/7358e86fd7e89b5f3206af58e266fd2d332759ed/modules/gltf/src/lib/encoders/encode-glb.ts

const MAGIC_glTF = 0x46546c67; // glTF in ASCII
const MAGIC_JSON = 0x4e4f534a; // JSON in ASCII
const MAGIC_BIN  = 0x004e4942; // BIN\0 in ASCII

/**
 * Thrown when a .gltf file references external resource files that weren't provided.
 */
export class MissingFilesError extends Error {
  constructor(
    public readonly missingFiles: string[],
    public readonly gltfFile: File,
    public readonly foundResources: File[]
  ) {
    super(`Missing resource files: ${missingFiles.join(', ')}`);
    this.name = 'MissingFilesError';
  }
}

/**
 * Parse a .gltf JSON file and return the list of external resource URIs
 * (i.e. buffer/image URIs that are not embedded data: URIs).
 */
export async function parseGltfExternalRefs(file: File): Promise<string[]> {
  const text = await file.text();
  let json: GLTF;
  try {
    json = JSON.parse(text) as GLTF;
  } catch {
    throw new Error(`Invalid .gltf file: ${file.name}`);
  }

  const refs = new Set<string>();

  for (const buffer of json.buffers ?? []) {
    if (buffer.uri && !buffer.uri.startsWith('data:')) {
      refs.add(buffer.uri);
    }
  }

  for (const image of json.images ?? []) {
    if (image.uri && !image.uri.startsWith('data:')) {
      refs.add(image.uri);
    }
  }

  return Array.from(refs);
}

/**
 * Pack a GLTF JSON object + pre-merged binary buffer into a GLB ArrayBuffer.
 * https://www.khronos.org/registry/glTF/specs/2.0/glTF-2.0.html#glb-file-format-specification-general
 */
export function createGlb(gltf: GLTF, binaryBuffers: Uint8Array): ArrayBuffer {
  gltf.buffers = [{ byteLength: binaryBuffers.byteLength }];

  const paddedJson = getGLTFPaddedBytes(gltf);

  // Global header (12) + JSON chunk header (8) + JSON + BIN chunk header (8) + BIN
  const totalByteLength = 12 + 8 + paddedJson.byteLength + 8 + binaryBuffers.byteLength;

  const glb = new DataView(new ArrayBuffer(totalByteLength));
  let byteOffset = 0;

  // Global header (magic, version, length)
  glb.setUint32(byteOffset, MAGIC_glTF, true); byteOffset += 4;
  glb.setUint32(byteOffset, 2, true);           byteOffset += 4;
  glb.setUint32(byteOffset, totalByteLength, true); byteOffset += 4;

  // JSON chunk (length, type, data)
  glb.setUint32(byteOffset, paddedJson.byteLength, true); byteOffset += 4;
  glb.setUint32(byteOffset, MAGIC_JSON, true);            byteOffset += 4;
  new Uint8Array(glb.buffer).set(paddedJson, byteOffset);
  byteOffset += paddedJson.byteLength;

  if (binaryBuffers.byteLength > 0) {
    // BIN chunk (length, type, data)
    glb.setUint32(byteOffset, binaryBuffers.byteLength, true); byteOffset += 4;
    glb.setUint32(byteOffset, MAGIC_BIN, true);                byteOffset += 4;
    new Uint8Array(glb.buffer).set(binaryBuffers, byteOffset);
  }

  return glb.buffer;
}

function getGLTFPaddedBytes(gltf: GLTF, byteOffset = 0): Uint8Array {
  const encoder = new TextEncoder();
  let string = JSON.stringify(gltf);

  const boundary = 4;
  const byteLength = encoder.encode(string).byteLength;
  const remainder = (byteOffset + byteLength) % boundary;
  const padding = remainder === 0 ? 0 : boundary - remainder;
  string += ' '.repeat(padding);

  return encoder.encode(string);
}

/**
 * Convert a .gltf file + its external resource files into a single .glb File.
 *
 * - External buffers (.bin) are merged into the GLB binary chunk.
 * - External images are embedded as base64 data URIs in the JSON chunk.
 */
export async function convertGltfToGlb(gltfFile: File, resources: File[]): Promise<File> {
  const text = await gltfFile.text();
  const gltf = JSON.parse(text) as GLTF;

  const resourcesByName = new Map(resources.map(f => [f.name, f]));

  // --- Step 1: Merge external buffers into one binary Uint8Array ---
  const bufferChunks: ArrayBuffer[] = [];
  const bufferOffsets: number[] = [];
  let totalBinaryLength = 0;

  for (const buffer of gltf.buffers ?? []) {
    if (buffer.uri && !buffer.uri.startsWith('data:')) {
      const file = resourcesByName.get(buffer.uri) ?? resourcesByName.get(buffer.uri.split('/').pop()!);
      if (!file) throw new Error(`Missing buffer file: ${buffer.uri}`);
      const data = await file.arrayBuffer();
      bufferOffsets.push(totalBinaryLength);
      bufferChunks.push(data);
      totalBinaryLength += align4(data.byteLength);
    } else {
      // Embedded buffer (data URI) — no binary to add
      bufferOffsets.push(totalBinaryLength);
    }
  }

  // --- Step 2: Re-target all bufferViews to buffer index 0 ---
  for (const bv of gltf.bufferViews ?? []) {
    const srcBuffer = bv.buffer ?? 0;
    bv.byteOffset = (bv.byteOffset ?? 0) + (bufferOffsets[srcBuffer] ?? 0);
    bv.buffer = 0;
  }

  // --- Step 3: Embed external images as base64 data URIs ---
  for (const image of gltf.images ?? []) {
    if (image.uri && !image.uri.startsWith('data:')) {
      const file = resourcesByName.get(image.uri) ?? resourcesByName.get(image.uri.split('/').pop()!);
      if (!file) throw new Error(`Missing image file: ${image.uri}`);
      const data = await file.arrayBuffer();
      const mimeType = file.type || mimeTypeFromName(image.uri);
      image.uri = `data:${mimeType};base64,${arrayBufferToBase64(data)}`;
    }
  }

  // --- Step 4: Concatenate buffer chunks ---
  const merged = new Uint8Array(totalBinaryLength);
  let offset = 0;
  for (const chunk of bufferChunks) {
    merged.set(new Uint8Array(chunk), offset);
    offset += align4(chunk.byteLength);
  }

  // --- Step 5: Build GLB ---
  const glbBuffer = createGlb(gltf, merged);
  const glbName = gltfFile.name.replace(/\.gltf$/i, '.glb');
  return new File([glbBuffer], glbName, { type: 'model/gltf-binary' });
}

// --- Helpers ---

function align4(n: number): number {
  return Math.ceil(n / 4) * 4;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function mimeTypeFromName(uri: string): string {
  const ext = uri.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'png':  return 'image/png';
    case 'webp': return 'image/webp';
    case 'ktx2': return 'image/ktx2';
    default:     return 'application/octet-stream';
  }
}
